"use client";

import { useTranslations } from "next-intl";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type ProfileTabBarProps = {
  listingsCount?: number;
  variant?: "default" | "compact";
  className?: string;
  sticky?: boolean;
  style?: React.CSSProperties;
};

function TabUnderline({ variant }: { variant: "default" | "compact" }) {
  const isCompact = variant === "compact";

  return (
    <div
      className={cn(
        "absolute left-1/2 w-14 -translate-x-1/2 rounded-full bg-primary",
        isCompact ? "bottom-0 h-0.5" : "bottom-0 h-1",
      )}
    />
  );
}

export function ProfileTabBar({
  listingsCount,
  variant = "default",
  className,
  sticky = true,
  style,
}: ProfileTabBarProps) {
  const t = useTranslations("profile.tabs");
  const isCompact = variant === "compact";
  const showShopCount = listingsCount !== undefined;

  const tabButtonClass = cn(
    "relative flex-1 text-sm font-medium transition-colors",
    isCompact ? "flex items-center justify-center pt-2 pb-1" : "py-3",
  );

  const shopLabel = (
    <>
      <span>{t("shop")}</span>
      {showShopCount ? (
        <span className="ml-1 text-xs text-muted-foreground">({listingsCount})</span>
      ) : null}
    </>
  );

  const shopTabClass = cn(tabButtonClass, "text-foreground");

  return (
    <div
      className={cn(
        isCompact ? "h-10 bg-transparent" : "border-b border-border/50 bg-background",
        sticky && "sticky top-[calc(var(--fixed-chrome-height,var(--header-height,0px)))] z-30",
        className,
      )}
      style={style}
    >
      <div className="flex">
        <button
          type="button"
          className={shopTabClass}
          aria-current="page"
        >
          {shopLabel}
          <TabUnderline variant={variant} />
        </button>

        <button
          type="button"
          disabled
          aria-disabled="true"
          className={cn(
            tabButtonClass,
            "cursor-not-allowed text-muted-foreground/50",
            !isCompact && "flex flex-col items-center justify-center gap-0.5",
          )}
        >
          <span>{t("wants")}</span>
          {!isCompact ? (
            <Badge
              variant="secondary"
              className="h-5 border-transparent px-1.5 py-0 text-[10px] font-normal normal-case tracking-wide text-muted-foreground"
            >
              {t("comingSoon")}
            </Badge>
          ) : null}
        </button>
      </div>
    </div>
  );
}
